import { axiosInstance } from '../axios/Axios';
import { AxiosPromise, AxiosRequestConfig } from 'axios';
import { deserialize, serialize } from './HalSerializer';
import { LinkModel } from "./LinkModel";

/**
 * HalIOConfig is used to communicate desired behavior to the various
 * HalIO functions.
 */
export interface HalIOConfig extends AxiosRequestConfig {

   /**
    * Set this option to true to force the underlying
    * framework to not handle errors and instead pass
    * the error up to the developer via the promise
    * catch clause.  This is generally not recommended.
    */
   disableErrorHandling?: boolean;

   /**
    * Set this option to true to force the underlying framework
    * to not handle client side errors (i.e. network errors,
    * connection timeouts) and instead pass the error up to the
    * developer via the promise catch clause.  This is generally
    * not recommended.
    */
   disableClientErrorHandling?: boolean;


   /**
    * Set this option to an array of error codes to force
    * the underlying framework to not handle server-side
    * errors for the error codes. The framework will
    * instead pass the error up to the developer via the
    * promise catch clause.  If this option is set to
    * true then no server side errors will be handled
    * by the underlying framework (404, 500, etc).
    */
   disableServerErrorHandling?: boolean | Array<number>;

   /**
    * Set this option to true to disable load masking
    * for long running client/server communications.
    */
   disableLoadMasking?: boolean;

   /**
    * A callback function that is called after the user
    * has acknowledged an error via the framework's
    * automatic error handling.  **The developer is
    * highly recommended to use this callback to make
    * sure that the user is not looking at a blank screen
    * after an error occurs.**
    */
   onErrorAcknowledged?: () => void;
}

interface ResponseHeader {
   ['content-type']?: string;
}

/**
 * HalIO is used for communicating with a REST API via the [HAL based media-type](http://stateless.co/hal_specification.html).
 *
 * This class is instantiated with a data model class to which it applies:
 *
 * ```typescript
 * const userIO: HalIO<User> = new HalIO(User);
 * ```
 *
 * The associated data model class should be defined with special property decorators to describe
 * how it is to be serialized/deserialized:
 *
 * * `@serializable` to designate what properties are serializable and the type to use for
 *   serialization/deserialization.  See [mobx serializr decorators](https://github.com/mobxjs/serializr)
 *   for more information.
 *
 * * `@link(key[,String | Array])` to designate what properties represent HAL _link info
 *   - Without an optional parameter, the property should be of type LinkModel:
 *     ```typescript
 *     @link("self")
 *     selfLink: LinkModel;
 *     ```
 *   - String means just return the href, the property should be a string type:
 *     ```typescript
 *     @link("self", String)
 *     selfHref: string;
 *     ```
 *   - Array means return an array of LinkModel types: Array&lt;LinkModel&gt;
 *     ```typescript
 *     @link("users:actions", Array)
 *     actions: LinkModel[];
 *     ```
 * * `@embedded(key, <ModelType>)` to designate what properties represent HAL _embedded info
 *   - ModelType is the type of model this embedded resource represents, which itself
 *     should be a decorated class.  The property should be of type Array&lt;ModelType&gt;:
 *     ```typescript
 *     @embedded("user:children", Children)
 *     children: Children[];
 *     ```
 * * `@optional` to designate that the property is optional
 *
 * Example:
 * ```typescript
 * class User {
 *
 *     @serializable
 *     name: string;
 *
 *     @serializable(isoDate())
 *     startDate: Date;
 *
 *     @serializable(list(number())
 *     agesOfChildren: number[];
 *
 *     @link("self", String)
 *     selfHref: string;
 *
 *     @link("user:actions", Array)
 *     actions: LinkModel[];
 *
 *     @embedded("user:children", Children)
 *     children: Children[];
 * }
 *
 * const userIO: HalIO<User> = new HalIO(User);
 * const config: HalIOConfig = {
 *    onErrorAcknowledged: () => {
 *        // Handle where to send user after they acknowledge an error so that they aren't
 *        // staring at a blank screen!
 *    };
 * };
 *
 * userIO.get(rootUrl, config).then(response => {
 *
 *    let user: User = response.data;
 *    // Do stuff here...
 *
 * }); // <-- We don't worry about having a catch clause on the promise because the underlying
 *     //     system pops up error dialog for us. See the onErrorAcknowledged comment above!
 * ```
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export class HalIO<T extends object> {
   private modelClass: new(...args: any[]) => T;

   /**
    * Usage:
    *
    * ```typescript
    * const userIO: HalIO<User> = new HalIO(User);
    * ```
    */
   constructor(modelClass: new(...args: any[]) => T) {
      this.modelClass = modelClass;
   }

   /**
    * Used similarly to Axios get
    *
    * @returns Returns the AxiosPromise with the data in response.data
    *
    * Usage:
    * ```typescript
    * const userIO: HalIO<User> = new HalIO(User);
    *
    * const config: HalIOConfig = {
    *    onErrorAcknowledged: () => {
    *        // Handle where to send user after they acknowledge an error so that they aren't
    *        // staring at a blank screen!
    *    };
    * };
    *
    * userIO.get(rootUrl, config).then(response => {
    *
    *    let user: User = response.data;
    *    // Do stuff here...
    *
    * }); // <-- We don't worry about having a catch clause on the promise because the underlying
    *     //     system pops up error dialog for us. See the onErrorAcknowledged comment above!
    * ```
    */
   get(url: string | LinkModel, config?: HalIOConfig): AxiosPromise<T> {
      const href: string = (typeof url === 'string') ? url : url.href;

      config = this.setHeader('Accept', 'application/hal+json', config);

      return axiosInstance.get(href, config)
                          .then(response => {
                             if (!this.contentTypeIsApplicationHalJson(response.headers)) {
                                throw new Error(
                                   `Expected Content-Type to be application/hal+json, got ${response.headers['content-type']}.`
                                );
                             }

                             response.data = deserialize(this.modelClass, response.data);

                             return response;
                          });
   }

   /**
    * Used similarly to Axios post
    *
    * @returns Returns the AxiosPromise with any data that may be returned in response.data
    *
    * Usage:
    * ```typescript
    * const userIO: HalIO<User> = new HalIO(User);
    *
    * const config: HalIOConfig = {
    *    onErrorAcknowledged: () => {
    *        // Handle where to send user after they acknowledge an error so that they aren't
    *        // staring at a blank screen!
    *    };
    * };
    *
    * userIO.post(rootUrl, myUser, config).then(response => {
    *
    *    let addedUser: User = response.data;
    *    // Do stuff here...
    *
    * }); // <-- We don't worry about having a catch clause on the promise because the underlying
    *     //     system pops up error dialog for us. See the onErrorAcknowledged comment above!
    * ```
    */
   post(url: string | LinkModel, data: T, config?: HalIOConfig): AxiosPromise<T> {
      const jsonData = serialize(data, this.modelClass);

      config = this.setHeader('Content-Type', 'application/json', config);

      const href: string = (typeof url === 'string') ? url : url.href;

      return axiosInstance.post(href, jsonData, config).then((response: any) => {

         if (this.contentTypeIsApplicationHalJson(response.headers)) {
            response.data = deserialize(this.modelClass, response.data);
         }

         return response;
      });
   }

   /**
    * Used similarly to Axios put
    *
    * @returns Returns the AxiosPromise with any data that may be returned in response.data
    *
    * Usage:
    * ```typescript
    * const userIO: HalIO<User> = new HalIO(User);
    *
    * const config: HalIOConfig = {
    *    onErrorAcknowledged: () => {
    *        // Handle where to send user after they acknowledge an error so that they aren't
    *        // staring at a blank screen!
    *    };
    * };
    *
    * userIO.put(rootUrl, myUser, config).then(response => {
    *
    *    let updatedUser: User = response.data;
    *    // Do stuff here...
    *
    * }); // <-- We don't worry about having a catch clause on the promise because the underlying
    *     //     system pops up error dialog for us. See the onErrorAcknowledged comment above!
    * ```
    */
   put(url: string | LinkModel, data: T, config?: HalIOConfig): AxiosPromise<T> {
      const jsonData = serialize(data, this.modelClass);

      config = this.setHeader('Content-Type', 'application/json', config);

      const href: string = (typeof url === 'string') ? url : url.href;

      return axiosInstance.put(href, jsonData, config).then((response: any) => {

         if (this.contentTypeIsApplicationHalJson(response.headers)) {
            response.data = deserialize(this.modelClass, response.data);
         }

         return response;
      });
   }

   /**
    * Used similarly to Axios delete
    *
    * @returns Returns an AxiosPromise
    *
    * Usage:
    * ```typescript
    * const userIO: HalIO<User> = new HalIO(User);
    *
    * const config: HalIOConfig = {
    *    onErrorAcknowledged: () => {
    *        // Handle where to send user after they acknowledge an error so that they aren't
    *        // staring at a blank screen!
    *    };
    * };
    *
    * userIO.delete(usersSelfLink, config).then(response => {
    *
    *    // Do stuff here...
    *
    * }); // <-- We don't worry about having a catch clause on the promise because the underlying
    *     //     system pops up error dialog for us. See the onErrorAcknowledged comment above!
    * ```
    */
   delete(url: string | LinkModel, config?: HalIOConfig): AxiosPromise {
      const href: string = (typeof url === 'string') ? url : url.href;

      return axiosInstance.delete(href, config);
   }

   private setHeader(name: string, value: string, config?: HalIOConfig): HalIOConfig {
      if (!config) {
         config = {};
      }
      if (!config.headers) {
         config.headers = {};
      }
      config.headers[name] = value;

      return config;
   }

   private contentTypeIsApplicationHalJson(headers: ResponseHeader): boolean {
      const contentType = headers != null ? headers['content-type'] : null;
      return (contentType != null && contentType.split(';')[0].trim() === 'application/hal+json');
   }
}
